/**
 * Le vocabulaire du produit : les listes fermées du PRD, à un seul endroit.
 *
 * Chaque code est accompagné de son libellé français **tel que le métier le dit**
 * (PRD §4.2 et §4.9). Les deux vivent ensemble délibérément : séparés, on obtient
 * un jour deux listes qui ne désignent plus les mêmes états — c'est la leçon des
 * motifs d'arrêt de bien-archiver, où code et couleur ont été réunis pour cette
 * raison.
 *
 * Ce fichier ne décide de rien : il transcrit. Aucun statut n'est inventé ici, et
 * il n'en manque aucun de ceux que le PRD énumère.
 */
/* ---------------------------------------------------------------------------
 * Le cycle de vie du chantier — PRD §4.9
 * ------------------------------------------------------------------------- */
/**
 * Les six statuts du chantier.
 *
 * *Refonte demandée par Chris le 19/08/2026 (`docs/statuts.docx`) : « les statuts de CE
 * ne sont pas clairs, il faut les renommer ».* Deux statuts ont disparu — « CE en cours »
 * (l'ancien, qui ne servait à rien) et « En cours » —, et les libellés sont désormais ceux
 * qu'il emploie.
 *
 * ## Trois se déduisent, trois se déclarent
 *
 * `ce_a_traiter`, `ce_validee` et `ce_demarree` **ne se saisissent pas** : ils tombent des
 * validations et de la date de démarrage (voir `cycle-de-vie.ts`). Les trois derniers se
 * posent à la main et l'emportent sur le calcul — une fois le chantier terminé, clôturé ou
 * arrêté, plus rien d'automatique ne doit le ramener en arrière.
 *
 * ## Pourquoi deux codes ont changé et pas les autres
 *
 * Un code ne se renomme que lorsqu'il **ment**, jamais pour l'élégance : il est écrit en
 * base, dans le journal et dans les migrations.
 *
 *   - `ce_validee_attente_date` disait « en attente d'une date » alors que ce statut ne
 *     s'atteint désormais **que si** une date existe. Devenu `ce_validee`.
 *   - `ce_initiale` désignait à la fois un statut de chantier et un `TYPES_VERSION` — deux
 *     sens sous un même mot dans la même application. Le statut devient `ce_demarree`, le
 *     type de version garde le sien.
 *
 * `fini`, `cloture` et `arrete` ne mentent pas : ils gardent leur code et changent de
 * libellé.
 *
 * `ce_en_cours` a été retiré et **son code ne sera jamais réemployé**, alors même que le
 * libellé « CE en cours » repart sur `ce_demarree` : d'anciennes lignes de journal le
 * portent, et le réutiliser leur donnerait rétroactivement un autre sens.
 */
export const STATUTS_CHANTIER = {
    ce_a_traiter: "CE à traiter",
    ce_validee: "CE validée par resp-exploit",
    ce_demarree: "CE en cours",
    fini: "CE terminée",
    cloture: "CE clôturée",
    arrete: "CE arrêtée / suspendue",
};
/**
 * Les statuts qui se déclarent à la main, et qui sont donc les seuls que la colonne
 * `chantier.statut` peut porter avec `ce_a_traiter`.
 *
 * *Décision de Chris, 19/08/2026 : « le statut sera mis à jour manuellement » pour ces
 * trois-là, et automatiquement pour les autres.*
 */
export const STATUTS_DECLARES = ["fini", "cloture", "arrete"];
/**
 * Ce que la colonne `chantier.statut` accepte — et rien d'autre (migration 036).
 *
 * `ce_a_traiter` y figure comme **valeur neutre** : « rien n'a été déclaré, la règle
 * décide ». C'est aussi ce qu'on repose pour annuler un « arrêté » posé par erreur.
 *
 * Les deux statuts déduits en sont absents **volontairement** : stockable, un statut
 * calculé finit stocké, puis divergent de son calcul, et plus personne ne sait lequel fait
 * foi. La base rend la divergence impossible plutôt que de la surveiller.
 */
export const STATUTS_STOCKABLES = ["ce_a_traiter", ...STATUTS_DECLARES];
/**
 * Le statut d'un chantier à l'instant de l'import.
 *
 * PRD §4.9, étape 1 : « une assistante intègre le DEPO (n° APO connu) ;
 * l'application archive automatiquement le devis initial et crée la copie de
 * travail ». L'import ne fait pas avancer le cycle de vie plus loin que ça.
 */
export const STATUT_A_L_IMPORT = "ce_a_traiter";
/* ---------------------------------------------------------------------------
 * Les versions de contre-étude — architecture §8.1
 * ------------------------------------------------------------------------- */
/**
 * Les cinq états figés ou dérivés qui gravitent autour d'un chantier.
 *
 * `devis` est la seule version que l'application ne produit pas : elle est la
 * transcription du fichier de l'ERP, et elle est figée dès sa création. Toutes les
 * autres en descendent, directement ou non.
 */
export const TYPES_VERSION = {
    devis: "Devis figé",
    travail: "Copie de travail",
    scenario: "Scénario",
    ce_initiale: "CE initiale",
    ce_suivi: "CE de suivi",
};
/* ---------------------------------------------------------------------------
 * Les statuts de ligne de contre-étude — PRD §4.2
 * ------------------------------------------------------------------------- */
/**
 * Ce qu'un conducteur a fait d'une ligne du devis.
 *
 * `conserve` est la valeur par défaut de la colonne : à la sortie de l'import,
 * **toutes** les lignes de la copie de travail sont conservées, puisque personne
 * n'y a encore touché.
 */
export const STATUTS_LIGNE = {
    conserve: "Conservé",
    remplace: "Remplacé",
    quantite_modifiee: "Quantité modifiée",
    prix_force: "Prix forcé",
    ajoute: "Ajouté",
    supprime: "Supprimé",
};
/** Le statut d'une ligne que personne n'a encore travaillée. */
export const STATUT_LIGNE_INITIAL = "conserve";
/* ---------------------------------------------------------------------------
 * Pourquoi un kilométrage manque — PRD §5.8
 * ------------------------------------------------------------------------- */
/**
 * Les six raisons pour lesquelles la distance entre deux villes n'est pas lisible.
 *
 * *Chris a tranché le 08/09/2026 : quand le km n'est pas calculable, le champ reste vide et
 * l'écran dit pourquoi.* Un 0 se lirait « c'est à côté » et fausserait tout contrôle d'heures
 * de transport — c'est la règle de la 058, où une distance inconnue ne déduit aucune zone
 * bien qu'elle affiche 0.
 *
 * Ils vivent ici, dans le moteur, et non dans le serveur qui les produit : c'est **l'écran**
 * qui les lit, et un motif rendu par l'un que l'autre ne saurait pas nommer s'afficherait en
 * code brut — « site_sans_table » sur la ligne d'un conducteur.
 *
 * **Aucun de ces six n'est une impasse depuis la 073** : le km se force à la main. Chacun est
 * donc formulé comme ce qu'il est — l'explication d'un champ vide qu'on peut remplir soi-même,
 * et non le refus d'une fonction.
 */
export const MOTIFS_SANS_KM = {
    villes_incompletes: "Choisir les deux villes pour obtenir la distance, ou saisir le km.",
    ville_libre: "La ville de chargement n'est pas au référentiel des distances : aucune table ne peut " +
        "répondre pour ce trajet, le km se saisit à la main.",
    aucun_site: "Aucune de ces deux villes n'est un site du groupe : les tables de distances ne donnent " +
        "que des distances depuis nos sites, il n'y a donc rien à lire pour ce trajet. Le km se " +
        "saisit à la main.",
    site_sans_table: "Ce site du groupe n'a pas encore de table de distances (menu Distance).",
    plusieurs_sites: "Deux sites du groupe partagent cette commune : désigner le site de chargement pour lever " +
        "l'ambiguïté.",
    distance_a_calculer: "La table de ce site ne porte pas encore cette commune : la distance reste à calculer.",
};
/**
 * Le motif tel qu'il arrive du serveur : une chaîne, qui peut ne rien désigner.
 *
 * Le contrat porte `motifSansKm: string | null` et non l'union fermée, parce qu'un écran servi
 * par une version de serveur plus récente recevrait un motif qu'il ne connaît pas. Il vaut
 * mieux une phrase générique qu'un écran blanc.
 */
export function expliquerLAbsenceDeKm(motif) {
    if (motif === null)
        return null;
    /*
      `Object.hasOwn` et non `in` : un essai l'a attrapé. `"toString" in MOTIFS_SANS_KM` est
      VRAI par héritage, et l'écran affichait alors le code source d'une fonction là où il
      attendait une phrase. Le motif vient du réseau — on ne choisit pas ce qu'il vaut.
    */
    return Object.hasOwn(MOTIFS_SANS_KM, motif)
        ? MOTIFS_SANS_KM[motif]
        : "La distance n'est pas calculable pour ce trajet.";
}
/**
 * Comment se dit une absence, en toutes lettres.
 *
 * *« La précision congé non validé ou validé est importante » (métier, 16/08/2026).* La nature ET
 * la validation, toujours ensemble : « congé » sans « validé » laisse croire que c'est acquis, et
 * « à confirmer » sans la nature ne dit pas s'il faut chercher un remplaçant ou trancher une
 * demande.
 */
export function direLAbsence(absence) {
    // Une machine ne dit pas « absente » : elle est immobilisée, et c'est le mot du SAV.
    if (absence.nature === "immobilisation")
        return "immobilisée";
    if (absence.nature === "arret") {
        return absence.valide ? "en arrêt" : "arrêt à confirmer";
    }
    if (absence.nature === "conge") {
        return absence.valide ? "congé validé" : "congé NON validé";
    }
    return absence.valide ? "absent" : "absence à confirmer";
}
/** Une absence validée est acquise : c'est elle qui demande une décision tout de suite. */
export function estAcquise(absence) {
    return absence.valide;
}
//# sourceMappingURL=vocabulaire.js.map